import React, { useMemo } from 'react';
import { useAuthStore } from '@store/authStore';
import { apiClient } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@components/ui/Skeleton';
import { RiMoneyDollarCircleLine } from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { getOperatorWithLocation } from '@/features/tails/utils';
import FinancialSummaryCards from '../components/FinancialSummaryCards';
import TopClientCard from '../components/TopClientCard';
import QuoteRatioCard from '../components/QuoteRatioCard';
import RecentReports from '../components/RecentReports';

const FinancialsPage = () => {
  const { userProfile, isLoading: isAuthLoading, isAuthenticated, fetchUserProfile } = useAuthStore();

  React.useEffect(() => {
    if (isAuthenticated && !userProfile && !isAuthLoading) {
      fetchUserProfile();
    }
  }, [isAuthenticated, userProfile, isAuthLoading, fetchUserProfile]);

  const profileId = userProfile?.userprofile_id;

  // Fetch all quotes for the current user
  const { data: allQuotes, isLoading } = useQuery({
    queryKey: ['financials-quotes', profileId],
    queryFn: async () => {
      const data = await apiClient.getQuotes(0, 200, { created_by: profileId });
      return data || [];
    },
    enabled: !!profileId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Accepted/closed quotes
  const acceptedQuotes = useMemo(() => {
    if (!allQuotes) return [];
    return allQuotes.filter(q => q.status === 'ACCEPTED');
  }, [allQuotes]);

  // Total revenue from accepted quotes
  const totalRevenue = useMemo(() => {
    return acceptedQuotes.reduce((sum, q) => sum + (parseFloat(q.total_due) || parseFloat(q.total_amount) || 0), 0);
  }, [acceptedQuotes]);

  // Placeholder commissions (15% of revenue — to be wired to real data later)
  const totalCommissions = useMemo(() => totalRevenue * 0.15, [totalRevenue]);

  // Top client by booking volume (count of accepted quotes)
  const topClient = useMemo(() => {
    if (acceptedQuotes.length === 0) return null;

    const clientCounts = {};
    for (const q of acceptedQuotes) {
      const client = q.client;
      if (!client) continue;
      const id = client.client_id || client.email;
      if (!id) continue;
      if (!clientCounts[id]) {
        clientCounts[id] = {
          name: [client.first_name, client.last_name].filter(Boolean).join(' ') || 'Unknown',
          count: 0,
        };
      }
      clientCounts[id].count++;
    }

    const sorted = Object.values(clientCounts).sort((a, b) => b.count - a.count);
    return sorted[0] || null;
  }, [acceptedQuotes]);

  // Recent reports — latest quotes (any status) with trip/bid info
  const recentReports = useMemo(() => {
    if (!allQuotes || allQuotes.length === 0) return [];

    return allQuotes
      .slice(0, 5)
      .map((q) => {
        const bid = q.bid_items?.[0]?.bid;
        const bidOption = bid?.bid_options?.[0];
        const operator = bidOption?.operator || bidOption?.aircraft?.operator;
        const client = q.client;

        return {
          tripNumber: bid?.trip_number || bid?.bid_number || `T-${q.quote_id?.slice(0, 6)}`,
          status: q.status || 'DRAFT',
          statusLabel: q.status === 'ACCEPTED' ? 'Received' : q.status === 'PENDING' ? 'Pending' : q.status || 'Draft',
          clientName: [client?.first_name, client?.last_name].filter(Boolean).join(' ') || 'N/A',
          operatorInfo: getOperatorWithLocation(operator),
        };
      });
  }, [allQuotes]);

  if (isLoading) {
    return (
      <div className="min-h-screen pb-20">
        <main className="max-w-7xl px-4 sm:px-6 lg:px-8 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-28 rounded-3xl" />
            <Skeleton className="h-28 rounded-3xl" />
          </div>
          <Skeleton className="h-24 rounded-3xl" />
          <Skeleton className="h-24 rounded-3xl" />
          <Skeleton className="h-48 rounded-3xl" />
        </main>
      </div>
    );
  }

  if (!allQuotes || allQuotes.length === 0) {
    return (
      <div className="min-h-screen">
        <main className="max-w-7xl px-4 sm:px-6 lg:px-8 py-5">
          <Card className="overflow-hidden">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <RiMoneyDollarCircleLine className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Financial Reports</h2>
              <p className="text-gray-500 max-w-md mx-auto">
                Financial data will appear here once you have quotes.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      <main className="max-w-7xl px-4 sm:px-6 lg:px-8 py-5 space-y-4">
        <FinancialSummaryCards
          totalRevenue={totalRevenue}
          totalCommissions={totalCommissions}
        />

        <TopClientCard clientName={topClient?.name} />

        <QuoteRatioCard
          closedTrips={acceptedQuotes.length}
          totalQuotes={allQuotes.length}
        />

        <RecentReports reports={recentReports} />
      </main>
    </div>
  );
};

export default FinancialsPage;
